import { Box, Button, IconButton } from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';
import { FOCUS_VISIBLE_RING } from '@/pages/restaurant/components/actions/ActionButtons';

export interface CompactActionButtonProps {
  icon: LucideIcon;
  /** Brand token var carried by the icon and border — one of
   *  `--color-icon-gold` / `--color-icon-teal` / `--color-icon-share`
   *  (src/theme/variables.css), matching ProfileActionBar's palette. */
  tokenVar: string;
  /** Background tint while `isActive` is true. */
  tint12: string;
  /** Background tint on hover/active press. */
  tint18: string;
  /** Toggleable actions (rate/bookmark) pass their persisted state; Share
   *  has no persistent active state and omits this. */
  isActive?: boolean;
  ariaLabel: string;
  ariaPressed?: boolean;
  onClick: (e: React.MouseEvent) => void | Promise<void>;
  /**
   * Button diameter in px. Defaults to the 44px Apple HIG / WCAG 2.5.5
   * touch-target floor — every surface this renders on (dense grid cards,
   * icon clusters over photos) must clear that floor, even where the
   * previous ad hoc IconButton undershot it (e.g. RestaurantCard's old
   * `size="xs"` ~24px hit box).
   */
  size?: number;
  /** Icon glyph size in px. Defaults to a proportion of `size` that mirrors
   *  ProfileActionBar's own ratio (28px icon / 56px button on mobile). */
  iconSize?: number;
  /**
   * Adds a semi-opaque dark scrim behind the border/icon so the brand token
   * color stays legible over photo or animated-gradient backgrounds
   * (TasteAuraBackground, list cover art) at every scroll/animation state —
   * a plain transparent fill can wash out on light frames of those
   * backgrounds. Off by default; surfaces sitting on a flat
   * `--card-bg`/canvas don't need it.
   */
  scrim?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  /** Passed through to the icon's own `style` — e.g. a call site's existing
   *  fill/stroke spring transition (Phase 1 motion token). Optional; most
   *  call sites don't need it. */
  iconStyle?: React.CSSProperties;
  /** Visible text label rendered beside the icon. Only used by
   *  `layout="fill"`; the circular icon-only layout ignores it. The
   *  `ariaLabel` must contain this text (WCAG 2.5.3 label-in-name). */
  label?: string;
  /**
   * `"icon"` (default): circular icon-only button, `size` px square.
   * `"fill"`: labeled button that fills its container's width (48px tall,
   * `--r-card-compact` radius) — the RestaurantCard action row, mirroring
   * ProfileActionBar's button treatment. Label color is `--card-text` since
   * it sits on `--card-bg`, not the canvas.
   */
  layout?: 'icon' | 'fill';
}

/**
 * Compact outlined-and-colored icon button — the Rate/Bookmark/Share visual
 * language from `ProfileActionBar` (restaurant profile page), sized down for
 * surfaces that can't afford a full-width labeled row: dense grid cards,
 * icon clusters over a photo, engagement bars, etc.
 *
 * Shares the brand tokens, the tint12/tint18 active/hover backgrounds, the
 * fill/stroke swap on `isActive`, and the project's standard focus-visible
 * ring with `ProfileActionBar` — do not re-derive any of that per call site.
 * `ProfileActionBar.tsx` itself is intentionally not built on top of this
 * (paire-app#132 — it's the original, already-shipped treatment this
 * component generalizes from; left untouched).
 */
export function CompactActionButton({
  icon: Icon,
  tokenVar,
  tint12,
  tint18,
  isActive = false,
  ariaLabel,
  ariaPressed,
  onClick,
  size = 44,
  iconSize,
  scrim = false,
  isLoading = false,
  isDisabled = false,
  iconStyle,
  label,
  layout = 'icon',
}: CompactActionButtonProps) {
  const isFill = layout === 'fill';
  const resolvedIconSize = iconSize ?? (isFill ? 20 : Math.round(size * 0.41));
  // Fill/stroke swap shared by both layouts. Black stroke on the filled state
  // clears >= 8.4:1 against the gold and teal fills (see note below).
  const glyph = (
    <Icon
      size={resolvedIconSize}
      fill={isActive ? tokenVar : 'none'}
      // Matches ProfileActionBar/RestaurantCard's existing filled-icon
      // pattern: once fill and stroke are the same brand hue, the glyph's
      // own detail strokes disappear into the fill. Black clears >= 8.4:1
      // against both the gold and teal fills.
      stroke={isActive ? 'black' : tokenVar}
      strokeWidth={2}
      style={{ flexShrink: 0, ...iconStyle }}
    />
  );

  if (isFill) {
    return (
      <Button
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        onClick={onClick}
        isLoading={isLoading}
        isDisabled={isDisabled}
        width="100%"
        minW={0}
        height="48px"
        // Tight px/gap so the 3-up card row fits "Saved"/"Share" at 320px
        // (~85px per button: 20px icon + 6px gap + ~42px widest label + 4px pad).
        px={1}
        gap={1.5}
        whiteSpace="nowrap"
        border="2px solid"
        borderColor={tokenVar}
        borderRadius="var(--r-card-compact)"
        bg={isActive ? tint12 : 'transparent'}
        color="var(--card-text)"
        fontSize="sm"
        fontWeight={600}
        transition="background-color 150ms ease"
        _hover={{ bg: tint18 }}
        _active={{ bg: tint18 }}
        sx={{ _focusVisible: { ...FOCUS_VISIBLE_RING, boxShadow: 'none' } }}
      >
        {glyph}
        {/* minW={0} on the button + this truncating span keep long labels
            (large text / zoom) from spilling out of the 3-up grid cell. */}
        <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {label}
        </Box>
      </Button>
    );
  }

  return (
    <IconButton
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      onClick={onClick}
      isLoading={isLoading}
      isDisabled={isDisabled}
      variant="unstyled"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minW={`${size}px`}
      minH={`${size}px`}
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      border="2px solid"
      borderColor={tokenVar}
      bg={isActive ? tint12 : scrim ? 'var(--color-overlay-30)' : 'transparent'}
      transition="background-color 150ms ease"
      _hover={{ bg: tint18 }}
      _active={{ bg: tint18 }}
      sx={{ _focusVisible: { ...FOCUS_VISIBLE_RING, boxShadow: 'none' } }}
    >
      {glyph}
    </IconButton>
  );
}
